import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import FormProcessingSpinner from "../../common/FormProcessingSpinner";
import http from "../../../helpers/http";
import { doObjToFormData } from "../../../helpers/helpers";
import { resendEmail } from "../../../states/actions/fetchSignup";
import { TOAST_SETTINGS } from "../../../utils/siteSettings";
import useDocumentTitle from "../../../hooks/useDocumentTitle";

/**
 * Standalone "verify your email" page.
 *
 * The verification step used to exist only inside /signup, gated on two
 * localStorage values, so no URL reached it. Anyone who closed that tab had no
 * way back - and sign-in told them their account did not exist, because
 * authenticateEmail() filters on mem_verified = 1. A member reported exactly
 * that on 8 Oct 2026 and asked whether the site was legitimate.
 *
 * Built to the forgot-password layout: single centred column, sm_screen_log,
 * one action. Nothing is fetched, so it renders immediately.
 */
const VerifyEmail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const isResending = useSelector(
    (state) => state.fetchSignup.isResendingVerificationCode,
  );

  const [email, setEmail] = useState(
    params.get("email") || localStorage.getItem("email") || "",
  );
  const [typed, setTyped] = useState("");
  // Was this address actually awaiting verification when we arrived?
  // check-email-verified returns verified:true for ANY address with no
  // pending unverified account - including ones that do not exist - so
  // without this the first poll bounced every visitor to /signin.
  const [wasPending, setWasPending] = useState(null);

  useDocumentTitle("Verify Email - UK Visa Jobs");

  // Poll while they go to their inbox, so clicking the link in another tab
  // lands them on sign-in rather than leaving them staring at this page.
  useEffect(() => {
    if (!email) return undefined;

    const interval = setInterval(() => {
      http
        .post("auth/check-email-verified", doObjToFormData({ email }))
        .then(({ data }) => {
          if (data?.verified === false) {
            setWasPending(true);
            return;
          }
          if (!data?.verified) return;
          clearInterval(interval);
          clearTimeout(timeout);
          // Only a transition from pending to verified means they just
          // clicked the link. Verified on the very first poll means there
          // was never anything to verify - stay put and let them read.
          if (wasPending) {
            localStorage.removeItem("email");
            localStorage.removeItem("isFirstStepCompleted");
            toast.success("Email verified. You can sign in now.", TOAST_SETTINGS);
            navigate("/signin");
          }
        })
        .catch(() => {
          /* transient - keep polling */
        });
    }, 3000);

    // Same 30-minute ceiling as the signup step; no reason to poll forever.
    const timeout = setTimeout(() => clearInterval(interval), 30 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [email, navigate, wasPending]);

  const handleResend = (e) => {
    e.preventDefault();
    if (email) dispatch(resendEmail({ email }));
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    const v = typed.trim();
    if (!v) return;
    setEmail(v);
    dispatch(resendEmail({ email: v }));
  };

  return (
    <>
      <ToastContainer />
      <section id="logon" className="sm_screen_log">
        <div className="contain">
          <div className="in_col">
            {email ? (
              <form action="" method="POST" onSubmit={handleResend}>
                <div className="log_blk">
                  <div className="txt">
                    <h2>Verify Email</h2>
                  </div>
                  <div className="form_row row">
                    <div className="col-sm-12">
                      <p className="verify_note">
                        We’ve sent a verification link to{" "}
                        <span className="verify_addr">{email}</span>. Please check
                        your inbox and click the link to activate your account.
                        Sometimes activation links may end up in the spam or
                        junk folder.
                      </p>
                    </div>
                  </div>
                  <div className="btn_blk form_btn form_blk">
                    <button
                      type="submit"
                      className="site_btn block"
                      disabled={isResending}
                    >
                      <FormProcessingSpinner isFormProcessing={isResending} />
                      Resend verification link
                    </button>
                  </div>
                  <div className="forgot">
                    Not your address? <Link to="/signup">Sign up again</Link>
                  </div>
                  <div className="forgot">
                    Already verified? <Link to="/signin">Sign in</Link>
                  </div>
                </div>
              </form>
            ) : (
              <form action="" method="POST" onSubmit={handleEmailSubmit}>
                <div className="log_blk">
                  <div className="txt">
                    <h2>Verify Email</h2>
                  </div>
                  <div className="form_row row">
                    <div className="col-sm-12">
                      <h5>Email Address</h5>
                      <div className="form_blk">
                        <input
                          type="text"
                          name="email"
                          id="email"
                          value={typed}
                          onChange={(ev) => setTyped(ev.target.value)}
                          className="input"
                          placeholder="Type your email addres here"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="btn_blk form_btn form_blk">
                    <button
                      type="submit"
                      className="site_btn block"
                      disabled={isResending}
                    >
                      <FormProcessingSpinner isFormProcessing={isResending} />
                      Send verification link
                    </button>
                  </div>
                  <div className="forgot">
                    Already verified? <Link to="/signin">Sign in</Link>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default VerifyEmail;
